import {
	faCircleCheck,
	faCircleExclamation,
	faLock,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PaymentLinkType,
	PublicPaymentLinkState,
} from '@supply-mover-2-0/shared';
import { resolveBrandPalette } from '@supply-mover-2-0/shared';
import { hexToRgb, useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { telHref } from '../../lib/supportContact';
import { usePortalSiteStore } from '../../stores/portalSiteStore';

/**
 * PD-16674 — the frame around the public payment-link pages: the brand's
 * header and a single centered column, with none of the signed-in storefront's
 * navigation (the visitor is not signed in and may never have an account).
 *
 * The header follows PortalHeader's rule for which wordmark to show: the
 * light-ink logo only when the header surface is itself light.
 *
 * While mounted it sets the page's referrer policy to `strict-origin`, so the
 * third-party scripts these pages load (the card fields, Turnstile) are only
 * ever told the storefront's origin, never the link in the address bar.
 */
export function PaymentLinkShell({
	children,
}: {
	children: ReactNode;
}): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const navigate = useNavigate();
	const site = usePortalSiteStore((s) => s.site);

	useEffect(() => {
		const meta = document.createElement('meta');
		meta.name = 'referrer';
		meta.content = 'strict-origin';
		document.head.appendChild(meta);
		return (): void => {
			meta.remove();
		};
	}, []);

	const headerBg = resolveBrandPalette(
		{ primaryColor: site?.primaryColor ?? null },
		isDark ? 'dark' : 'light',
	).header;
	const headerRgb = headerBg != null ? hexToRgb(headerBg) : null;
	const headerIsLight =
		headerRgb != null &&
		(0.299 * headerRgb.r + 0.587 * headerRgb.g + 0.114 * headerRgb.b) / 255 >
			0.5;
	const logoUrl =
		headerIsLight &&
		site?.logoUrlLight != null &&
		site.logoUrlLight.length > 0
			? site.logoUrlLight
			: (site?.logoUrl ?? null);
	const brandName = site?.displayName ?? null;

	return (
		<div className="min-h-dvh bg-brand-page-bg flex flex-col">
			<header className="bg-brand-primary text-brand-primary-fg border-b border-brand-primary-fg/10 px-4 sm:px-6 pt-[env(safe-area-inset-top)]">
				<div className="max-w-2xl mx-auto h-16 flex items-center justify-between gap-4">
					{logoUrl != null && logoUrl !== '' ? (
						<img
							src={logoUrl}
							alt={t(brandName ?? 'Brand logo')}
							className="h-8 object-contain"
						/>
					) : (
						<span className="font-bold text-lg">{t(brandName ?? '')}</span>
					)}
					<div className="flex items-center gap-4">
						<span className="flex items-center gap-2 text-sm text-brand-primary-fg/85">
							<FontAwesomeIcon icon={faLock} />
							<span className="hidden sm:inline">{t('Secure payment')}</span>
						</span>
						{/* Customers who have a storefront login can see every
						    invoice and their full balance there. The root route
						    shows sign-in to a visitor who is not signed in, as the
						    other public pages' Sign in buttons do. */}
						<button
							type="button"
							onClick={() => {
								void navigate('/');
							}}
							className="text-sm font-medium text-brand-primary-fg border border-brand-primary-fg/40 rounded-md px-3 py-1.5 hover:bg-brand-primary-fg/10 transition-colors"
						>
							{t('Sign in')}
						</button>
					</div>
				</div>
			</header>
			<main className="flex-1 px-4 py-6 sm:py-10">
				<div className="max-w-2xl mx-auto">{children}</div>
			</main>
		</div>
	);
}

/** The brand's support phone and email, when the storefront has them. */
export function PaymentLinkSupportLine(): JSX.Element | null {
	const { t } = useTranslation();
	const contact = usePortalSiteStore((s) => s.site?.supportContact ?? null);
	if (contact == null || (contact.phone == null && contact.email == null)) {
		return null;
	}
	return (
		<p className="text-sm text-brand-muted">
			{t('Questions? Contact us')}
			{contact.phone != null ? (
				<>
					{' '}
					<a
						href={telHref(contact.phone)}
						className="text-portal-accent hover:underline"
					>
						{t(contact.phone)}
					</a>
				</>
			) : null}
			{contact.phone != null && contact.email != null ? ` ${t('or')}` : null}
			{contact.email != null ? (
				<>
					{' '}
					<a
						href={`mailto:${contact.email}`}
						className="text-portal-accent hover:underline"
					>
						{t(contact.email)}
					</a>
				</>
			) : null}
			{t('.')}
		</p>
	);
}

const STATE_COPY: Record<
	Exclude<PublicPaymentLinkState, 'active'>,
	{ title: string; body: string }
> = {
	// Worded for what the link covers, never the whole account: the customer
	// may still owe other invoices this link does not include.
	paid: {
		title: 'Paid in full',
		body: 'Everything this link covers has been paid. Thank you.',
	},
	expired: {
		title: 'This link has expired',
		body: 'For your security, links only work for a limited time. Please contact us for a new one.',
	},
	revoked: {
		title: 'This link is no longer active',
		body: 'It has been withdrawn. Please contact us if you still need it.',
	},
	locked: {
		title: 'This link is locked',
		body: 'It was locked after several unsuccessful attempts, to protect your account. Please contact us for a new one.',
	},
	unavailable: {
		title: 'This is not available right now',
		body: 'Online payment is not available at the moment. Please contact us.',
	},
	invalid: {
		title: 'This link is not valid',
		body: 'Check that you opened the whole link from your email, or contact us for a new one.',
	},
};

/** A friendly, branded page for every state but active. */
/** The paid wording when the page knows what kind of link it is. */
const PAID_COPY_BY_TYPE: Partial<
	Record<PaymentLinkType, { title: string; body: string }>
> = {
	invoice: {
		title: 'This invoice is fully paid',
		body: 'Thank you for your payment.',
	},
	statement: {
		title: 'These invoices are fully paid',
		body: 'Every invoice this link covers has been paid. Thank you.',
	},
};

export function PaymentLinkStatePanel({
	state,
	linkType,
}: {
	state: Exclude<PublicPaymentLinkState, 'active'>;
	/** Known when the link loaded; picks the paid wording. */
	linkType?: PaymentLinkType;
}): JSX.Element {
	const { t } = useTranslation();
	const copy =
		state === 'paid' && linkType != null
			? (PAID_COPY_BY_TYPE[linkType] ?? STATE_COPY.paid)
			: STATE_COPY[state];
	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3">
			<FontAwesomeIcon
				icon={state === 'paid' ? faCircleCheck : faCircleExclamation}
				className={`text-4xl ${state === 'paid' ? 'text-success' : 'text-brand-muted'}`}
			/>
			<h1 className="text-2xl font-semibold text-default">{t(copy.title)}</h1>
			<p className="text-sm text-brand-muted">{t(copy.body)}</p>
			<div className="pt-2 flex justify-center">
				<PaymentLinkSupportLine />
			</div>
		</div>
	);
}
